// T-081: moved from wwwroot/ds/rules.js and typed.
// T-053 (design D5): the browser mirror of Soren.Platform.Core/Validation/InputRules.cs (T-111). Same rules, same codes; the messages
// come from /ds/rules.fa.json (the C# catalogue), never from here. Pure functions: node runs them against the same
// case table as the C# tests (tests/OJE.Tests/Validation/rule-cases.json), so the two cannot drift apart.

/** What a rule answers: a message code (null = valid), the normalised value, and for a card or IBAN the bank's key. */
export interface RuleResult {
  code: string | null;
  value: string | null;
  bank: string | null;
}

/** A bank of the directory /ds/banks.json (the C# BankDirectory). */
export interface Bank {
  key: string;
  ibanCode: string;
  bins: string[];
}

export function latinDigits(v: unknown): string {
  return String(v ?? '')
    .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660));
}
export const digitsOnly = (v: unknown): string => latinDigits(v).replace(/\D/g, '');
export const persianText = (v: unknown): string =>
  String(v ?? '')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/ى/g, 'ی')
    .trim();
const ok = (value: string, bank?: string | null): RuleResult => ({ code: null, value, bank: bank ?? null });
const fail = (code: string): RuleResult => ({ code, value: null, bank: null });

export function luhn(d: string): boolean {
  let s = 0;
  for (let i = 0; i < d.length; i++) {
    let x = +d[d.length - 1 - i];
    if (i % 2) {
      x *= 2;
      if (x > 9) x -= 9;
    }
    s += x;
  }
  return s % 10 === 0;
}
function mod97(d: string): number {
  let r = 0;
  for (const c of d) r = (r * 10 + +c) % 97;
  return r;
}

export function makeRules(banks?: Bank[] | null) {
  const byCode = new Map<string, Bank>(),
    byBin = new Map<string, Bank>();
  for (const b of banks || []) {
    byCode.set(b.ibanCode, b);
    for (const bin of b.bins) byBin.set(bin, b);
  }
  return {
    money(v: unknown, min = 10000, max = 50000000000): RuleResult {
      const raw = latinDigits(v)
        .replace(/[,٬، ]/g, '')
        .trim();
      if (!raw) return fail('required');
      if (!/^\d+$/.test(raw)) return fail('money.format');
      const n = Number(raw);
      if (n < min) return fail('money.min');
      if (n > max) return fail('money.max');
      return ok(String(n));
    },
    card(v: unknown): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      if (d.length !== 16) return fail('card.length');
      if (!luhn(d)) return fail('card.luhn');
      return ok(d, byBin.get(d.slice(0, 6))?.key);
    },
    cardLast4(v: unknown): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      return d.length === 4 ? ok(d) : fail('card.last4');
    },
    iban(v: unknown): RuleResult {
      let t = latinDigits(v).toUpperCase().replace(/[ -]/g, '');
      if (t.startsWith('IR')) t = t.slice(2);
      if (!t) return fail('required');
      if (t.length !== 24 || !/^\d+$/.test(t)) return fail('iban.length');
      if (mod97(t.slice(2) + '1827' + t.slice(0, 2)) !== 1) return fail('iban.checksum');
      return ok('IR' + t, byCode.get(t.slice(2, 5))?.key);
    },
    nationalCode(v: unknown): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      if (d.length !== 10) return fail('ncode.length');
      if (new Set(d).size === 1) return fail('ncode.checksum');
      let s = 0;
      for (let i = 0; i < 9; i++) s += +d[i] * (10 - i);
      const r = s % 11,
        k = +d[9];
      return (r < 2 ? k === r : k === 11 - r) ? ok(d) : fail('ncode.checksum');
    },
    mobile(v: unknown): RuleResult {
      let d = digitsOnly(v);
      if (!d) return fail('required');
      if (d.startsWith('0098')) d = '0' + d.slice(4);
      else if (d.startsWith('98') && d.length === 12) d = '0' + d.slice(2);
      else if (d.startsWith('9') && d.length === 10) d = '0' + d;
      return /^09\d{9}$/.test(d) ? ok(d) : fail('mobile.format');
    },
    postalCode(v: unknown): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      if (d.length !== 10) return fail('postal.length');
      if (/[02]/.test(d.slice(0, 5)) || new Set(d).size === 1) return fail('postal.invalid');
      return ok(d);
    },
    jalaliDate(v: unknown, todayIso?: string): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      if (d.length !== 8) return fail('date.format');
      const y = +d.slice(0, 4),
        m = +d.slice(4, 6),
        day = +d.slice(6);
      if (y < 1300 || y > 1500 || m < 1 || m > 12 || day < 1 || day > jalaliMonthLength(y, m))
        return fail('date.invalid');
      const iso = jalaliToIso(y, m, day);
      if (todayIso && iso > todayIso) return fail('date.future');
      return ok(iso);
    },
    time(v: unknown): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      if (d.length !== 4 || +d.slice(0, 2) > 23 || +d.slice(2) > 59) return fail('time.invalid');
      return ok(d.slice(0, 2) + ':' + d.slice(2));
    },
    reference(v: unknown): RuleResult {
      const d = digitsOnly(v);
      if (!d) return fail('required');
      return d.length >= 6 && d.length <= 20 ? ok(d) : fail('ref.length');
    },
    percent(v: unknown): RuleResult {
      const raw = latinDigits(v).replace('٫', '.').replace('/', '.').trim();
      if (!raw) return fail('required');
      if (!/^\d+(\.\d*)?$/.test(raw)) return fail('percent.range');
      const p = Number(raw);
      if (p < 0 || p > 100) return fail('percent.range');
      if (raw.includes('.') && raw.split('.')[1].length > 1) return fail('percent.precision');
      return ok(String(p));
    },
    email(v: unknown): RuleResult {
      const t = String(v ?? '').trim();
      if (!t) return fail('required');
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t) ? ok(t) : fail('email.format');
    },
    password(v: unknown): RuleResult {
      const text = String(v ?? '');
      if (!text) return fail('required');
      return passwordParts(text).every(Boolean) ? ok(text) : fail('password.weak');
    },
  };
}

/** [length ≥ 10, lower+upper, digit, symbol]: the strength meter shows how many hold. */
export function passwordParts(v: string): boolean[] {
  return [v.length >= 10, /[a-z]/.test(v) && /[A-Z]/.test(v), /\d/.test(latinDigits(v)), /[^A-Za-z0-9\s]/.test(v)];
}

// ---------- Jalali calendar (jalaali-js algorithm) ----------
const BRK = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
];
const div = (a: number, b: number): number => ~~(a / b),
  mod = (a: number, b: number): number => a - ~~(a / b) * b;
function jalCal(jy: number): { leap: number; gy: number; march: number } {
  // eslint-disable-next-line prefer-const -- one declaration list where the other names are reassigned
  let gy = jy + 621,
    leapJ = -14,
    jp = BRK[0],
    jm: number,
    jump = 0,
    n: number,
    i: number;
  for (i = 1; i < BRK.length; i++) {
    jm = BRK[i];
    jump = jm - jp;
    if (jy < jm) break;
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }
  n = jy - jp;
  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ++;
  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150,
    march = 20 + leapJ - leapG;
  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
  let leap = mod(mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}
export function g2d(gy: number, gm: number, gd: number): number {
  const d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408;
  return d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
}
export function d2g(jdn: number): { gy: number; gm: number; gd: number } {
  let j = 4 * jdn + 139361631;
  j += div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1,
    gm = mod(div(i, 153), 12) + 1;
  return { gy: div(j, 1461) - 100100 + div(8 - gm, 6), gm, gd };
}
export function j2d(jy: number, jm: number, jd: number): number {
  const r = jalCal(jy);
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}
export function d2j(jdn: number): { jy: number; jm: number; jd: number } {
  const gy = d2g(jdn).gy;
  let jy = gy - 621;
  const r = jalCal(jy);
  let k = jdn - g2d(gy, 3, r.march);
  if (k >= 0) {
    if (k <= 185) return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
    k -= 186;
  } else {
    jy--;
    k += 179;
    if (r.leap === 1) k++;
  }
  return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
}
export const jalaliMonthLength = (jy: number, jm: number): number =>
  jm <= 6 ? 31 : jm <= 11 ? 30 : jalCal(jy).leap === 0 ? 30 : 29;
export const weekCol = (jdn: number): number => mod(jdn + 2, 7); // 0 = Saturday … 6 = Friday
const pad = (n: number): string => String(n).padStart(2, '0');
export function jalaliToIso(jy: number, jm: number, jd: number): string {
  const g = d2g(j2d(jy, jm, jd));
  return g.gy + '-' + pad(g.gm) + '-' + pad(g.gd);
}
export function isoToJdn(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return g2d(y, m, d);
}
export function jdnToIso(jdn: number): string {
  const g = d2g(jdn);
  return g.gy + '-' + pad(g.gm) + '-' + pad(g.gd);
}
